import { useEffect, useRef, useState } from 'react';
import { Check, ChevronRight } from 'lucide-react';
import { admin, palette, radii, typography } from '../../../styles/tokens';
import { ALL_ITEMS } from '../config/setupGuideConfig';
import type { ResolvedItem } from '../hooks/useSetupGuideState';

interface ChecklistRowProps {
  item: ResolvedItem;
  index: number;
  onClick: (item: ResolvedItem) => void;
}

function titleForKey(key: string): string {
  return ALL_ITEMS.find(i => i.key === key)?.title ?? key;
}

export default function ChecklistRow({ item, index, onClick }: ChecklistRowProps) {
  const [hover, setHover] = useState(false);
  const [showBlockerTooltip, setShowBlockerTooltip] = useState(false);
  const dismissTimer = useRef<number | null>(null);
  const complete = item.result.complete;
  const blocked = item.blocked;

  useEffect(() => {
    if (!showBlockerTooltip) return;
    const onAnyClick = () => setShowBlockerTooltip(false);
    // Defer attach so the click that opened it doesn't immediately close it.
    const t = window.setTimeout(() => {
      window.addEventListener('click', onAnyClick, { once: true });
    }, 0);
    dismissTimer.current = window.setTimeout(() => setShowBlockerTooltip(false), 4000);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('click', onAnyClick);
      if (dismissTimer.current) window.clearTimeout(dismissTimer.current);
    };
  }, [showBlockerTooltip]);

  const handleClick = () => {
    if (blocked) {
      setShowBlockerTooltip(true);
      return;
    }
    onClick(item);
  };

  const prerequisiteTitles = item.blockerKeys.map(titleForKey);
  const blockerMessage =
    prerequisiteTitles.length > 0
      ? `Finish the items above first — you need ${prerequisiteTitles.join(', ')} before this one.`
      : 'Finish the items above first.';

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={handleClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          width: '100%',
          padding: '16px 12px',
          border: 'none',
          borderRadius: radii.md,
          background: hover && !blocked ? palette.g50 : 'transparent',
          cursor: blocked ? 'default' : 'pointer',
          textAlign: 'left',
          transition: 'background 150ms ease',
          fontFamily: 'inherit',
          opacity: blocked ? 0.5 : 1,
        }}
      >
        <div
          style={{
            flexShrink: 0,
            width: 32,
            height: 32,
            borderRadius: radii.full,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: complete ? palette.green : 'transparent',
            border: complete ? `1px solid ${palette.green}` : `1.5px solid ${palette.g300}`,
            color: complete ? palette.white : admin.caption,
            fontSize: typography.size.caption,
            fontWeight: typography.weight.semibold,
          }}
        >
          {complete && !blocked ? <Check size={16} strokeWidth={3} /> : index + 1}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: typography.size.body,
              fontWeight: typography.weight.semibold,
              color: admin.header,
              textDecoration: complete ? 'line-through' : 'none',
              textDecorationColor: complete ? palette.g400 : undefined,
              marginBottom: 2,
            }}
          >
            {item.config.title}
          </div>
          <div
            style={{
              fontSize: typography.size.caption,
              color: admin.caption,
              lineHeight: typography.lineHeight.normal,
            }}
          >
            {item.config.subtitle}
          </div>
          {complete && item.result.reason === 'seeded' && (
            <div
              style={{
                fontSize: 13,
                color: palette.g500,
                lineHeight: typography.lineHeight.normal,
                marginTop: 2,
              }}
            >
              Pre-filled based on your industry — worth a quick look
            </div>
          )}
        </div>

        <ChevronRight size={18} color={admin.muted} style={{ flexShrink: 0 }} />
      </button>

      {showBlockerTooltip && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 12,
            right: 12,
            marginTop: 4,
            padding: '10px 14px',
            background: palette.g700 ?? '#1f2937',
            color: palette.white,
            fontSize: typography.size.caption,
            lineHeight: typography.lineHeight.normal,
            borderRadius: radii.md,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10,
            animation: 'fadeIn 150ms ease',
          }}
        >
          {blockerMessage}
        </div>
      )}
    </div>
  );
}
